import {useContext, useEffect, useId, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import Button from "../../../../components/Button.jsx";
import useDownloadState from "../../../../api/utilities/useDownloadState.js";
import LoadingComponent from "../../../../components/LoadingComponent.jsx";
import {GlobalContext} from "../../../../utils/context/GlobalContextProvider.jsx";
import {ProjectIntegrationsApi} from "../../../../api/authority/generated/index";
import toast from "react-hot-toast";
import {useNavigate} from "react-router-dom";
import {useErrorTranslation} from "../../../../api/utilities/translateErrorMessage.js";
import useAuthorityConfiguration from "../../../../api/utilities/useAuthorityConfiguration.js";
import Select from "react-select";
import TextareaWithClipboard from "../../../../components/TextareaWithClipboard.jsx";
import useIntegrationScript from "../IntegrationFormInput/useIntegrationScript.js";
import {IntegrationActions, IntegrationSection} from "../IntegrationDetailUi.jsx";
import StoredSecretInput from "../IntegrationFormInput/StoredSecretInput.jsx";

function BsShopIntegrationFormInput({type, label, value, note, onChange, secretConfigured = false}) {

    let id = useId();

    return <div className={'flex flex-col gap-1.5'}>
            <label className={'text-sm font-medium text-slate-700 dark:text-slate-200'} htmlFor={'mysql-lead-' + id}>
                {label}
            </label>
            {type === 'password' ? <StoredSecretInput
                configured={secretConfigured}
                value={value}
                onChange={onChange}
                concealWhileEditing={true}
            /> : <input
                type={type}
                id={'mysql-lead-' + id}
                className={'form-input w-full'}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />}
        {note && type !== 'password' && <div className={'text-xs leading-5 text-slate-500 dark:text-slate-400'}>{note}</div>}
    </div>;
}

export default function BsShopIntegrationForm({project, projectIntegration}) {

    let {t} = useTranslation();
    let {et} = useErrorTranslation();
    let navigate = useNavigate();
    let integrationScript = useIntegrationScript('bsshop', projectIntegration?.id);

    let [bsShopSettings, setBsShopSettings] = useState({
        'hostname': projectIntegration?.hostname ?? '',
        'port': projectIntegration?.port ?? '1433',
        'username': projectIntegration?.username ?? '',
        'password': '',
        'databaseName': projectIntegration?.databaseName ?? '',
        'skippedEmailAddressList': projectIntegration?.skippedEmailAddressList ?? '',
        'b2cCentres': projectIntegration?.b2cCentres ?? [],
        'b2bCentres': projectIntegration?.b2bCentres ?? [],
    });

    let {resetCurrentUserProjects} = useContext(GlobalContext);

    let authorityConfiguration = useAuthorityConfiguration();

    let [buttonClicked, setButtonClicked] = useState(false);
    let [state, setDownloading, setResult, setError] = useDownloadState();

    useEffect(() => {

        if (!buttonClicked) {
            return;
        }

        setButtonClicked(false);

        setDownloading(true);

        new ProjectIntegrationsApi(authorityConfiguration)
            .bSShopIntegration({
                projectId: project.id,
                integrationId: projectIntegration?.id ?? null,
                bsShopIntegrationInput: bsShopSettings,
            })
            .then((result) => {
                navigate('/project/' + project.id + '/settings/integrations?integrationId=' + result.id);
                resetCurrentUserProjects();

                if (projectIntegration === null) {
                    toast.success(t('created'));
                } else {
                    if (result.errors.length > 0) {
                        toast.success(t('saved-with-errors'));
                    } else {
                        toast.success(t('saved'));
                    }
                }
            })
            .catch(e => {
                et(e).then((message) => { if (message) toast.error(message); });
            })
            .finally(() => setDownloading(false));

    }, [buttonClicked]);

    let centreOptions = useMemo(() => {
        let result = [];

        for (let centreId in projectIntegration?.knownCentres ?? []) {
            let centreName = projectIntegration?.knownCentres[centreId];

            result.push({
                value: centreId,
                label: centreName
            });
        }

        return result;
    }, [projectIntegration?.knownCentres]);

    let defaultB2BCentres = useMemo(() => {
        let result = [];

        for (let centreId in projectIntegration?.knownCentres ?? []) {
            let centreName = projectIntegration?.knownCentres[centreId];

            if (
                projectIntegration.b2bCentres.includes(centreId.toString())
                || projectIntegration.b2bCentres.includes(parseInt(centreId))
            ) {
                result.push({
                    value: centreId,
                    label: centreName
                });
            }

        }

        return result;
    }, [projectIntegration?.knownCentres, projectIntegration?.b2bCentres]);


    let defaultB2CCentres = useMemo(() => {
        let result = [];

        for (let centreId in projectIntegration?.knownCentres ?? []) {
            let centreName = projectIntegration?.knownCentres[centreId];

            if (
                projectIntegration.b2cCentres.includes(centreId.toString())
                || projectIntegration.b2cCentres.includes(parseInt(centreId))
            ) {
                result.push({
                    value: centreId,
                    label: centreName
                });
            }
        }

        return result;
    }, [projectIntegration?.knownCentres, projectIntegration?.b2cCentres]);

    function setSelectedB2CCentres(value) {
        let selectedCentres = [];

        for (let valueObject of Object.values(value)) {
            selectedCentres.push(valueObject.value);
        }

        setBsShopSettings(
            (bsShopSettings) => (
                {...bsShopSettings, b2cCentres: selectedCentres}
            )
        );
    }

    function setSelectedB2BCentres(value) {
        let selectedCentres = [];

        for (let valueObject of Object.values(value)) {
            selectedCentres.push(valueObject.value);
        }

        setBsShopSettings(
            (bsShopSettings) => (
                {...bsShopSettings, b2bCentres: selectedCentres}
            )
        );
    }

    // noinspection JSUnresolvedReference
    return <div className={'flex flex-col gap-5'}>
        {projectIntegration !== null && <IntegrationSection
            title={t('integration.bsshop.script-title')}
            description={t('integration.eprivacy-warning')}
        >
            <TextareaWithClipboard
                value={integrationScript}
                showButton={true}
            />
        </IntegrationSection>}

        <LoadingComponent loading={state.downloading}>
            <IntegrationSection
                title={'Připojení k BSShop'}
                description={'Nastavte databázové připojení. Heslo ponechte prázdné, pokud uloženou hodnotu nechcete změnit.'}
            >
                <div className={'grid gap-4 md:grid-cols-2'}>
                    <BsShopIntegrationFormInput
                        type={'text'}
                        label={t('mysql.hostname')}
                        value={bsShopSettings.hostname}
                        onChange={(value) => setBsShopSettings((bsShopSettings) => {
                            return {
                                ...bsShopSettings, hostname: value,
                            };
                        })}
                    />
                    <BsShopIntegrationFormInput
                        type={'integer'}
                        label={t('port')}
                        value={bsShopSettings.port}
                        onChange={(value) => setBsShopSettings((bsShopSettings) => {
                            return {
                                ...bsShopSettings, port: value,
                            };
                        })}
                    />
                    <BsShopIntegrationFormInput
                        type={'text'}
                        label={t('auth.user')}
                        value={bsShopSettings.username}
                        onChange={(value) => setBsShopSettings((bsShopSettings) => {
                            return {
                                ...bsShopSettings, username: value,
                            };
                        })}
                    />
                    <BsShopIntegrationFormInput
                        type={'password'}
                        label={t('auth.password')}
                        value={bsShopSettings.password}
                        secretConfigured={projectIntegration?.passwordConfigured ?? false}
                        onChange={(value) => setBsShopSettings((bsShopSettings) => {
                            return {
                                ...bsShopSettings, password: value,
                            };
                        })}
                    />
                    <BsShopIntegrationFormInput
                        type={'text'}
                        label={t('database.database-name')}
                        value={bsShopSettings.databaseName}
                        onChange={(value) => setBsShopSettings((bsShopSettings) => {
                            return {
                                ...bsShopSettings, databaseName: value,
                            };
                        })}
                    />
                </div>

                {projectIntegration && <div className={'mt-5 grid gap-4'}>
                        <label className={'flex flex-col gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-200'}>
                            {t('bs-shop.pohoda.enabled-b2c-centres')}
                            <Select
                                defaultValue={defaultB2CCentres}
                                isMulti
                                name="enabledCentres"
                                options={centreOptions}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                placeholder={null}
                                noOptionsMessage={() => t('project.integration-no-options')}
                                onChange={setSelectedB2CCentres}
                            />
                        </label>
                        <label className={'flex flex-col gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-200'}>
                            {t('bs-shop.pohoda.enabled-b2b-centres')}
                            <Select
                                defaultValue={defaultB2BCentres}
                                isMulti
                                name="enabledCentres"
                                options={centreOptions}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                placeholder={null}
                                noOptionsMessage={() => t('project.integration-no-options')}
                                onChange={setSelectedB2BCentres}
                            />
                        </label>
                        <label className={'flex flex-col gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-200'}>
                            {t('bs-shop.pohoda.excluded-email')}
                            <textarea
                                value={bsShopSettings.skippedEmailAddressList}
                                name="skipped-emails"
                                className={'form-textarea min-h-24 w-full rounded-lg'}
                                onChange={(e) => setBsShopSettings((bsShopSettings) => ({
                                    ...bsShopSettings,
                                    skippedEmailAddressList: e.target.value,
                                }))}
                            />
                            <span className={'text-xs font-normal leading-5 text-slate-500 dark:text-slate-400'}>
                                {t('bs-shop.pohoda.excluded-email-delimiter-info')}
                            </span>
                        </label>
                    </div>}

                <div className={'mt-5'}>
                    <IntegrationActions>
                        <Button
                            className={'button-primary'}
                            onClick={() => setButtonClicked(true)}
                            title={projectIntegration ? 'Uložit změny' : 'Vytvořit integraci'}
                        >
                            {projectIntegration ? 'Uložit změny' : 'Vytvořit integraci'}
                        </Button>
                    </IntegrationActions>
                </div>
            </IntegrationSection>
        </LoadingComponent>
    </div>;
}
